import { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetDescription,
} from "./ui/sheet";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import IconPicker, { resolveIcon } from "./IconPicker";
import ColorPicker from "./ColorPicker";
import api from "../lib/api";
import { Trash, ArrowUpRight, Image as ImageIcon, Sparkle, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";

const SIZE_OPTIONS = [
  { id: "1x1", label: "1×1", desc: "Pequeño" },
  { id: "2x1", label: "2×1", desc: "Ancho" },
  { id: "2x2", label: "2×2", desc: "Grande" },
];

const STYLE_OPTIONS = [
  { id: "icon", label: "Ícono", desc: "Botón clásico" },
  { id: "rich", label: "Tarjeta", desc: "Vista previa" },
];

const DEFAULT_FORM = {
  name: "",
  url: "",
  icon: "Globe",
  color: "#2563EB",
  text_color: "#FFFFFF",
  size: "1x1",
  image_url: "",
  style: "icon",
  preview_image: "",
  preview_title: "",
  preview_description: "",
  theme_color: "",
  site_name: "",
  favicon: "",
};

function getDomain(url) {
  try {
    const u = /^https?:\/\//i.test(url) ? url : "https://" + url;
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function PreviewTile({ form }) {
  const Icon = resolveIcon(form.icon);
  const sizeClass =
    form.size === "2x2" ? "w-44 h-44" : form.size === "2x1" ? "w-56 h-28" : "w-32 h-32";

  if (form.style === "rich" && (form.preview_image || form.preview_title)) {
    const bgColor = form.theme_color || form.color || "#1A1C29";
    const domain = getDomain(form.url);
    return (
      <div
        data-testid="tile-preview"
        className={`deck-tile rounded-3xl overflow-hidden relative ${sizeClass} flex flex-col justify-end`}
        style={{ backgroundColor: bgColor }}
      >
        {form.preview_image && (
          <img
            src={form.preview_image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
        <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/40 flex items-center justify-center backdrop-blur-sm z-[2]">
          <ArrowUpRight size={13} weight="bold" className="text-white" />
        </div>
        {form.favicon && (
          <div className="absolute top-3 left-3 w-7 h-7 rounded-lg bg-white/95 flex items-center justify-center z-[2] overflow-hidden">
            <img src={form.favicon} alt="" className="w-4 h-4 object-contain" />
          </div>
        )}
        <div className="relative p-3 z-[1]">
          <div className="font-heading font-bold text-white text-sm leading-tight line-clamp-2 drop-shadow">
            {form.preview_title || form.name || "Título"}
          </div>
          {domain && (
            <div className="text-[9px] font-mono uppercase tracking-[0.1em] text-white/70 mt-1 truncate">
              {domain}
            </div>
          )}
        </div>
      </div>
    );
  }

  // icon style
  return (
    <div
      data-testid="tile-preview"
      className={`deck-tile rounded-3xl flex flex-col justify-between p-4 relative ${sizeClass}`}
      style={{ backgroundColor: form.color, color: form.text_color }}
    >
      <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/30 flex items-center justify-center backdrop-blur-sm">
        <ArrowUpRight size={13} weight="bold" />
      </div>
      <div className="flex-1 flex items-center justify-center">
        {form.image_url ? (
          <img src={form.image_url} alt="" className="max-w-[60%] max-h-[60%] object-contain" />
        ) : (
          <Icon size={form.size === "2x2" ? 72 : 40} weight="fill" />
        )}
      </div>
      <div className="font-mono text-[11px] font-bold uppercase tracking-[0.08em] truncate">
        {form.name || "MI BOTÓN"}
      </div>
    </div>
  );
}

export default function TileEditor({ open, onOpenChange, tile, deckId, onSaved }) {
  const isEdit = !!tile;
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const lastFetchedUrl = useRef("");

  useEffect(() => {
    if (open) {
      lastFetchedUrl.current = "";
      if (tile) {
        setForm({
          name: tile.name || "",
          url: tile.url || "",
          icon: tile.icon || "Globe",
          color: tile.color || "#2563EB",
          text_color: tile.text_color || "#FFFFFF",
          size: tile.size || "1x1",
          image_url: tile.image_url || "",
          style: tile.style || "icon",
          preview_image: tile.preview_image || "",
          preview_title: tile.preview_title || "",
          preview_description: tile.preview_description || "",
          theme_color: tile.theme_color || "",
          site_name: tile.site_name || "",
          favicon: tile.favicon || "",
        });
        lastFetchedUrl.current = tile.url || "";
      } else {
        setForm(DEFAULT_FORM);
      }
    }
  }, [open, tile]);

  const fetchPreview = async (rawUrl, opts = {}) => {
    const url = (rawUrl || "").trim();
    if (!url) {
      toast.error("Pon un URL primero");
      return;
    }
    setFetchingPreview(true);
    try {
      const { data } = await api.get(`/preview?url=${encodeURIComponent(url)}`);
      lastFetchedUrl.current = url;
      setForm((f) => ({
        ...f,
        preview_image: data.image || "",
        preview_title: data.title || "",
        preview_description: data.description || "",
        theme_color: data.theme_color || "",
        site_name: data.site_name || "",
        favicon: data.favicon || "",
        // Auto-fill name if empty
        name: f.name || data.site_name || data.title || "",
        // If user just clicked the magic button, switch to rich
        style: opts.switchToRich ? "rich" : f.style,
        // Auto-set size 2x1 when switching to rich for first time
        size: opts.switchToRich && f.size === "1x1" ? "2x1" : f.size,
      }));
      if (opts.switchToRich) toast.success("Vista previa enriquecida");
      else toast.success("Vista previa cargada");
    } catch (e) {
      toast.error("No se pudo obtener la vista previa");
    } finally {
      setFetchingPreview(false);
    }
  };

  // Auto-fetch on URL blur
  const handleUrlBlur = () => {
    const url = form.url.trim();
    if (!url) return;
    if (lastFetchedUrl.current === url) return;
    if (!form.preview_image && !form.preview_title) {
      // silent fetch in background - only if we don't have any preview yet
      fetchPreview(url);
    }
  };

  const validate = () => {
    if (!form.name.trim()) {
      toast.error("Pon un nombre al botón");
      return false;
    }
    if (!form.url.trim()) {
      toast.error("Añade un URL");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      let url = form.url.trim();
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url;
      }
      const payload = { ...form, url };
      if (!payload.image_url) delete payload.image_url;
      // Strip empty optional preview fields
      ["preview_image", "preview_title", "preview_description", "theme_color", "site_name", "favicon"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (isEdit) {
        const { data } = await api.put(`/tiles/${tile.tile_id}`, payload);
        onSaved(data, "update");
      } else {
        const { data } = await api.post("/tiles", { ...payload, deck_id: deckId });
        onSaved(data, "create");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo guardar el botón");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("¿Eliminar este botón?")) return;
    try {
      await api.delete(`/tiles/${tile.tile_id}`);
      onSaved(tile.tile_id, "delete");
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-deck-sidebar border-white/10 text-white w-full sm:max-w-md overflow-y-auto"
        data-testid="tile-editor"
      >
        <SheetHeader>
          <SheetTitle className="text-white font-heading text-2xl">
            {isEdit ? "Editar botón" : "Nuevo botón"}
          </SheetTitle>
          <SheetDescription className="text-white/50 font-mono text-xs uppercase tracking-[0.15em]">
            {isEdit ? "Personaliza tu acceso rápido" : "Crea un acceso rápido"}
          </SheetDescription>
        </SheetHeader>

        {/* Live preview */}
        <div className="mt-6 mb-2 flex items-center justify-center">
          <PreviewTile form={form} />
        </div>

        <div className="space-y-5 mt-4">
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">URL</Label>
            <div className="flex gap-2">
              <Input
                data-testid="tile-url-input"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                onBlur={handleUrlBlur}
                placeholder="https://youtube.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 flex-1"
              />
              <Button
                type="button"
                onClick={() => fetchPreview(form.url, { switchToRich: true })}
                disabled={fetchingPreview}
                className="bg-[#2563EB] text-white hover:bg-[#1D4ED8] font-heading font-bold whitespace-nowrap"
                data-testid="tile-magic-btn"
                title="Generar vista previa"
              >
                <MagicWand size={16} weight="fill" className="mr-1" />
                {fetchingPreview ? "..." : "Auto"}
              </Button>
            </div>
            <p className="text-[10px] font-mono text-white/40">
              Pulsa Auto para extraer imagen, título y color del sitio.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">Nombre</Label>
            <Input
              data-testid="tile-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="YouTube"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          {/* Style toggle */}
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
              Estilo
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {STYLE_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`style-opt-${s.id}`}
                  onClick={() => setForm({ ...form, style: s.id })}
                  className={`p-3 rounded-xl border transition flex flex-col items-start gap-0.5 ${
                    form.style === s.id
                      ? "bg-[#2563EB]/15 border-[#2563EB] text-[#FACC15]"
                      : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                  }`}
                >
                  <span className="font-heading font-bold text-sm flex items-center gap-1.5">
                    {s.id === "rich" && <Sparkle size={12} weight="fill" />}
                    {s.label}
                  </span>
                  <span className="text-[10px] font-mono uppercase opacity-70">{s.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <Tabs defaultValue={form.style === "rich" ? "preview" : "color"} className="w-full">
            <TabsList className="grid grid-cols-4 bg-white/5 border border-white/10">
              <TabsTrigger value="preview" data-testid="tab-preview">Preview</TabsTrigger>
              <TabsTrigger value="color" data-testid="tab-color">Color</TabsTrigger>
              <TabsTrigger value="icon" data-testid="tab-icon">Ícono</TabsTrigger>
              <TabsTrigger value="size" data-testid="tab-size">Tamaño</TabsTrigger>
            </TabsList>

            <TabsContent value="preview" className="pt-4 space-y-3">
              <div className="space-y-2">
                <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                  Imagen de portada (URL)
                </Label>
                <Input
                  data-testid="tile-preview-image-input"
                  value={form.preview_image}
                  onChange={(e) => setForm({ ...form, preview_image: e.target.value })}
                  placeholder="https://..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                  Título de la tarjeta
                </Label>
                <Input
                  data-testid="tile-preview-title-input"
                  value={form.preview_title}
                  onChange={(e) => setForm({ ...form, preview_title: e.target.value })}
                  placeholder="Sales Hub"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
              <Button
                type="button"
                onClick={() => fetchPreview(form.url, { switchToRich: true })}
                disabled={fetchingPreview}
                variant="outline"
                className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10"
                data-testid="tile-refresh-preview-btn"
              >
                <MagicWand size={14} className="mr-2" />
                {fetchingPreview ? "Cargando..." : "Re-generar desde URL"}
              </Button>
            </TabsContent>

            <TabsContent value="color" className="pt-4">
              <ColorPicker
                value={form.color}
                textColor={form.text_color}
                onChange={({ color, text_color }) =>
                  setForm({ ...form, color, text_color })
                }
              />
            </TabsContent>
            <TabsContent value="icon" className="pt-4">
              <IconPicker
                value={form.icon}
                onChange={(icon) => setForm({ ...form, icon, image_url: "" })}
              />
              <div className="mt-4 space-y-2">
                <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                  O usa una imagen (URL)
                </Label>
                <Input
                  data-testid="tile-image-input"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://...png"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
            </TabsContent>
            <TabsContent value="size" className="pt-4">
              <div className="grid grid-cols-3 gap-3">
                {SIZE_OPTIONS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`size-opt-${s.id}`}
                    onClick={() => setForm({ ...form, size: s.id })}
                    className={`p-4 rounded-xl border transition flex flex-col items-center gap-1 ${
                      form.size === s.id
                        ? "bg-[#2563EB]/15 border-[#2563EB] text-[#FACC15]"
                        : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                    }`}
                  >
                    <div className="font-heading font-bold text-lg">{s.label}</div>
                    <div className="text-[10px] font-mono uppercase">{s.desc}</div>
                  </button>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <SheetFooter className="mt-8 flex-col gap-2 sm:flex-col">
          <Button
            data-testid="tile-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] font-heading font-bold py-6"
          >
            {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear botón"}
          </Button>
          {isEdit && (
            <Button
              data-testid="tile-delete-btn"
              variant="outline"
              onClick={handleDelete}
              className="w-full bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <Trash size={16} className="mr-2" /> Eliminar botón
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
